// src/components/admin/AdminLeaderboards.js
//
// ===========================================================================
// WHAT WAS WRONG
// ===========================================================================
// The previous version was:
//
//     getDocs(collectionGroup(db, "leaderBoard_matterrock"))
//
// with the comment "// Or loop over known slugs". So the admin leaderboard
// page showed exactly one hardcoded topic, forever, no matter what you were
// trying to look at. Every other topic on the platform was unreachable from
// this screen.
//
// It was also unprotected at the route level, which mattered more here than
// on the student page — this is the view that shows every score for a topic
// across every school.
//
// ===========================================================================
// WHY THERE IS A TEXT BOX AND NOT A DROPDOWN
// ===========================================================================
// Firestore has no client-side "list all collections" API. Because scores
// live in one top-level collection PER TOPIC (leaderBoard_{slug}), there is
// no way for the browser to discover which of them exist. A slug has to come
// from somewhere.
//
// So: type a slug, or pick one from the recent list, which is kept in
// localStorage on this machine. Recently-viewed is genuinely how this screen
// gets used — an admin is nearly always checking the topic they just got an
// email about.
//
// If you'd rather have a real dropdown, the fix is a registry document —
// have UniversalQuizGame.saveToLeaderboard also setDoc a
// `leaderboardIndex/{slug}` marker on write. One extra write per game
// completion buys you a listable index. Say the word and I'll add it.

import React, { useCallback, useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { format } from "date-fns";

const RECENT_KEY = "adminLeaderboardRecentSlugs";
const FETCH_LIMIT = 200;

const loadRecent = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(raw) ? raw.slice(0, 8) : [];
  } catch {
    return [];
  }
};

const normalizeSlug = (raw = "") =>
  String(raw).trim().toLowerCase().replace(/^leaderboard_/i, "").replace(/\s+/g, "");

export default function AdminLeaderboards() {
  const db = getFirestore();

  const [slugInput, setSlugInput] = useState("");
  const [activeSlug, setActiveSlug] = useState("");
  const [rows, setRows] = useState([]);
  const [recent, setRecent] = useState(loadRecent);
  const [bestOnly, setBestOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const rememberSlug = (slug) => {
    const next = [slug, ...recent.filter((s) => s !== slug)].slice(0, 8);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* private browsing — not worth failing the page over */
    }
  };

  const load = useCallback(
    async (rawSlug) => {
      const slug = normalizeSlug(rawSlug);
      if (!slug) return;

      setLoading(true);
      setError("");
      setActiveSlug(slug);

      try {
        const snap = await getDocs(
          query(
            collection(db, `leaderBoard_${slug}`),
            orderBy("totalScore", "desc"),
            limit(FETCH_LIMIT)
          )
        );

        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (snap.size > 0) rememberSlug(slug);
      } catch (err) {
        console.error("[AdminLeaderboards] load failed:", err);
        setError(
          `Couldn't read leaderBoard_${slug}. Check the slug spelling, or that this account has admin rights.`
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [db, recent]
  );

  // Deep-link support: /admin/leaderboards?slug=matterrock
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("slug");
    if (fromUrl) {
      setSlugInput(fromUrl);
      load(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every attempt is its own document, so the raw list is dominated by
  // whoever replayed the most. Default to one row per player.
  const visible = bestOnly
    ? [
        ...rows
          .reduce((map, row) => {
            const key = row.uid || row.playerId || row.id;
            if (!map.has(key)) map.set(key, row);
            return map;
          }, new Map())
          .values(),
      ]
    : rows;

  return (
    <div style={s.page}>
      <h2 style={s.h2}>📊 Leaderboard Explorer</h2>

      <div style={s.controls}>
        <input
          style={s.input}
          value={slugInput}
          placeholder="Topic slug, e.g. matterrock"
          onChange={(e) => setSlugInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(slugInput)}
        />
        <button style={s.btn} onClick={() => load(slugInput)} disabled={loading}>
          {loading ? "Loading…" : "Load"}
        </button>

        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={bestOnly}
            onChange={(e) => setBestOnly(e.target.checked)}
          />
          Best run per player
        </label>
      </div>

      {recent.length > 0 && (
        <div style={s.recent}>
          <span style={s.recentLabel}>Recent:</span>
          {recent.map((slug) => (
            <button
              key={slug}
              style={{
                ...s.chip,
                ...(slug === activeSlug ? s.chipOn : null),
              }}
              onClick={() => {
                setSlugInput(slug);
                load(slug);
              }}
            >
              {slug}
            </button>
          ))}
        </div>
      )}

      {error && <p style={s.error}>{error}</p>}

      {activeSlug && !loading && !error && (
        <p style={s.meta}>
          <strong>leaderBoard_{activeSlug}</strong> — {rows.length} total{" "}
          {rows.length === FETCH_LIMIT ? `(capped at ${FETCH_LIMIT})` : "attempts"}
          {bestOnly && `, ${visible.length} unique players`}
        </p>
      )}

      {visible.length > 0 && (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Player</th>
              <th style={s.thNum}>Total</th>
              <th style={s.thNum}>Quiz</th>
              <th style={s.thNum}>Bonus</th>
              <th style={s.th}>Subject</th>
              <th style={s.th}>When</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={row.id} style={i % 2 ? s.trAlt : undefined}>
                <td style={s.td}>{i + 1}</td>
                <td style={s.td}>{row.playerId || "—"}</td>
                <td style={s.tdNum}>
                  <strong>{row.totalScore ?? 0}</strong>
                </td>
                <td style={s.tdNum}>{row.quizScore ?? 0}</td>
                <td style={s.tdNum}>{row.bonusRoundScore ?? 0}</td>
                <td style={s.td}>{row.subject || "—"}</td>
                <td style={s.td}>
                  {row.timestamp?.toDate
                    ? format(row.timestamp.toDate(), "MMM d, yyyy h:mm a")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeSlug && !loading && !error && rows.length === 0 && (
        <p style={s.meta}>
          No scores recorded for <strong>{activeSlug}</strong> yet.
        </p>
      )}
    </div>
  );
}

// Inline styles rather than a stylesheet — this is an admin-only screen and
// a global .table / .chip / .input in a CSS file is exactly the class of bug
// documented in styles/game-shell.css.
const s = {
  page: { padding: 24, fontFamily: "system-ui, sans-serif", maxWidth: 1100, margin: "0 auto" },
  h2: { marginTop: 0 },
  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 },
  input: {
    padding: "10px 14px",
    fontSize: 15,
    borderRadius: 8,
    border: "2px solid #ccd",
    minWidth: 260,
  },
  btn: {
    padding: "10px 20px",
    fontSize: 15,
    fontWeight: 700,
    borderRadius: 8,
    border: "none",
    background: "#2196f3",
    color: "#fff",
    cursor: "pointer",
  },
  toggle: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, cursor: "pointer" },
  recent: { display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center", marginBottom: 14 },
  recentLabel: { fontSize: 13, color: "#667" },
  chip: {
    padding: "5px 12px",
    fontSize: 13,
    borderRadius: 999,
    border: "1px solid #ccd",
    background: "#f6f8fb",
    cursor: "pointer",
  },
  chipOn: { background: "#2196f3", color: "#fff", borderColor: "#2196f3" },
  meta: { fontSize: 14, color: "#556" },
  error: { color: "#c02b2b", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 14 },
  th: { textAlign: "left", padding: "9px 10px", borderBottom: "2px solid #dde", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#667" },
  thNum: { textAlign: "right", padding: "9px 10px", borderBottom: "2px solid #dde", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#667" },
  td: { padding: "9px 10px", borderBottom: "1px solid #eef" },
  tdNum: { padding: "9px 10px", borderBottom: "1px solid #eef", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  trAlt: { background: "#fafbfe" },
};
